/**
 * Dialog für den Auszahlungsplan eines Darlehens.
 * Nutzt DialogTemplate für einheitliches Layout und
 * delegiert die Fachlogik an LoanAuszahlungDetail.
 */
import React, { useRef, useState } from 'react';
import DialogTemplate from '../../../layout/DialogTemplate';
import Button from '../../../common/inputs/Button';
import LoanAuszahlungDetail, {
  LoanAuszahlungDetailHandle,
} from '../../../tables/LoanAuszahlungDetail';
import { LoanWithPlan } from '../../../../types';
import { formatNumber } from '../../../../utils/formatters';

interface LoanAuszahlungDialogProps {
  /** Steuert ob der Dialog geöffnet ist */
  open: boolean;
  /** Das Darlehen, dessen Auszahlungsplan bearbeitet wird */
  loan: LoanWithPlan | null;
  /** Label-Mapping für Darlehenstypen (vom Backend geladen) */
  typLabels: Record<string, string>;
  /** Projektlaufzeit in Jahren */
  projektLaufzeit: number;
  /** Ob Bearbeitung erlaubt ist */
  canEdit?: boolean;
  /** Callback zum Schließen */
  onClose: () => void;
  /** Callback wenn sich Auszahlungen geändert haben */
  onAuszahlungChanged?: () => void;
}

const LoanAuszahlungDialog: React.FC<LoanAuszahlungDialogProps> = ({
  open,
  loan,
  typLabels,
  projektLaufzeit,
  canEdit = true,
  onClose,
  onAuszahlungChanged,
}) => {
  const detailRef = useRef<LoanAuszahlungDetailHandle>(null);
  const [isSaving, setIsSaving] = useState(false);

  const title = loan
    ? `Auszahlungsplan: ${typLabels[loan.typ] || loan.typ} (${formatNumber(loan.darlehenshoehe ?? 0, 0)} €)`
    : '';

  return (
    <DialogTemplate
      open={open && loan !== null}
      onClose={onClose}
      title={title}
      maxWidth="sm"
      actions={
        canEdit ? (
          <>
            <Button
              variant="secondary"
              onClick={() => detailRef.current?.cancel()}
              disabled={isSaving}
            >
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={() => detailRef.current?.save()}
              disabled={isSaving}
            >
              {isSaving ? 'Speichern...' : 'Speichern'}
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Schließen
          </Button>
        )
      }
    >
      {loan && (
        <LoanAuszahlungDetail
          ref={detailRef}
          loan={loan}
          projektLaufzeit={projektLaufzeit}
          onAuszahlungChanged={onAuszahlungChanged}
          onClose={onClose}
          canEdit={canEdit}
          onSavingChange={setIsSaving}
        />
      )}
    </DialogTemplate>
  );
};

export default LoanAuszahlungDialog;
